import React, { useCallback, useMemo } from "react";
import { tryParseDate } from "../../../../../date";
import Priority from "../../../labels/priority/Priority";
import Source from "../../../labels/source/Source";
import Styles from "../TicketDetails.module.scss";
import {
    DetailRow,
    DetailSection,
    TicketProperties, sectionRegistry,
} from "./base";
import Status from "../../../labels/status/Status";
import AgentsDisplay from "../../../labels/user/Agents";
import Tags from "../../../labels/tags/Tags";
import Queue from "../../../labels/queue/Queue";
import Link from "../../../../theme/button/Link";
import { LoggingHandler } from "../../../../../contexts/logging/LoggingContext";

export default function GeneralSection({
    ticket, isMinimized, onToggleExpand, isReadOnly, metricPathPrefix,
}: TicketProperties) {
    ticket.useUpdates();

    const metricPath = useMemo(() => [...metricPathPrefix, "general"], metricPathPrefix);

    const handleAgentChange = useCallback(async (userId: string) => {
        await ticket.assign({
            assigneeId: userId,
        });
        ticket.highlight();
    }, [ticket]);

    return (
        <DetailSection title="General" isMinimized={isMinimized} onToggleExpand={onToggleExpand}>
            <DetailRow title="Assignee">
                <AgentsDisplay
                    user={ticket.chat.assignee}
                    shouldShowSelfFirst
                    defaultText="Unassigned"
                    onChange={!isReadOnly ? handleAgentChange : undefined}
                    allowOverlay={!isReadOnly}
                    metricPathPrefix={[...metricPath, "assignee"]}
                />
            </DetailRow>
            <DetailRow title="Source">
                <a
                    href={ticket.chat.source?.url as string}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Link to the source of the conversation."
                    onClick={LoggingHandler.instance.wrapMetric([...metricPath, "source", "click"])}
                >
                    <Source type={ticket.chat.source?.type} text={ticket.chat.source?.text} />
                </a>
            </DetailRow>
            <DetailRow title="Status">
                <Status ticket={ticket} />
            </DetailRow>
            {
                !isReadOnly && (
                    <DetailRow title="Priority">
                        <Priority
                            ticketPriority={ticket.priority}
                            onChange={priority => {
                                ticket.saveTicket({
                                    priority,
                                });
                                LoggingHandler.instance.metric([...metricPath, "priority", "change"]);
                            }}
                        />
                    </DetailRow>
                )
            }
            <DetailRow title="Created">
                {ticket.createDate?.toLocaleString()}
            </DetailRow>
            {ticket.ticket?.queue && (
                <DetailRow title="Queue">
                    <Queue ticket={ticket} />
                </DetailRow>
            )}

            {ticket.ticket?.closedDate && (
                <DetailRow title="Completed">
                    {tryParseDate(ticket.ticket?.closedDate)?.toLocaleString()}
                </DetailRow>
            )}

            {
                !isReadOnly && (
                    <DetailRow title="Tags" className="multiline">
                        <Tags
                            ticket={ticket}
                            className={Styles["detail-tags"]}
                            metricPathPrefix={[...metricPath, "tags"]}
                            onChange={tags => {
                                ticket.setTags(tags);
                            }}
                        />
                    </DetailRow>
                )
            }

        </DetailSection>
    );
}

sectionRegistry.register({
    sectionId: "urn:fq:portal-chat-section:general",
    label: "General",
    component: GeneralSection,
});
